import type { Activity, Deal, DealStage, DealStatus } from '@/types';

/**
 * Derive a deal's stall bucket from its last activity timestamp.
 * Sold deals never stall. <24h = active, 24-48h = slowing, >48h = no_activity.
 * The DB column `status` stays as raw 'active'/etc; this overlay is for UI only.
 */
export function stallBucket(lastActivityIso: string, stage: DealStage): DealStatus {
  if (stage === 'sold') return 'active';
  const last = new Date(lastActivityIso).getTime();
  if (!Number.isFinite(last)) return 'active';
  const hours = (Date.now() - last) / 3_600_000;
  if (hours >= 48) return 'no_activity';
  if (hours >= 24) return 'slowing';
  return 'active';
}

export type Momentum =
  | 'won'
  | 'ready_to_close'
  | 'heating_up'
  | 'at_risk'
  | 'slowing'
  | 'active'
  | 'cold';

export interface MomentumInfo {
  key: Momentum;
  label: string;
  /** Tailwind classes for a small pill (border + bg + text). */
  className: string;
  /** 'up' | 'down' | 'flat' */
  trend: 'up' | 'down' | 'flat';
}

export function computeMomentum(deal: Deal, activities: Activity[]): MomentumInfo {
  if (deal.stage === 'sold') {
    return { key: 'won', label: 'Won', trend: 'up',
      className: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' };
  }
  const now = Date.now();
  const last = new Date(deal.last_activity_at).getTime();
  const hours = Number.isFinite(last) ? (now - last) / 3_600_000 : 9999;
  const last48 = activities.filter(a =>
    a.deal_id === deal.id && (now - new Date(a.created_at).getTime()) <= 48 * 3_600_000
  ).length;

  if (deal.stage === 'closing' && hours <= 48) {
    return { key: 'ready_to_close', label: 'Ready to Close', trend: 'up',
      className: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' };
  }
  if (deal.priority === 'hot' && hours > 48) {
    return { key: 'at_risk', label: 'At Risk', trend: 'down',
      className: 'border-primary/50 bg-primary/15 text-primary' };
  }
  if (last48 >= 3) {
    return { key: 'heating_up', label: 'Heating Up', trend: 'up',
      className: 'border-primary/40 bg-primary/10 text-primary' };
  }
  if (hours >= 72) {
    return { key: 'cold', label: 'Cold', trend: 'down',
      className: 'border-border bg-muted/40 text-muted-foreground' };
  }
  if (hours >= 24) {
    return { key: 'slowing', label: 'Slowing', trend: 'down',
      className: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400' };
  }
  return { key: 'active', label: 'Active', trend: 'flat',
    className: 'border-blue-500/40 bg-blue-500/10 text-blue-300' };
}